/**
 * useIsAdmin — single source of truth for admin gating in the UI.
 *
 * A user is "admin" when ANY of the following is true:
 *  - their email matches the founder address, or
 *  - the localStorage flag `gi-engine.admin === 'true'` is set, or
 *  - the build-time env `VITE_ADMIN_MODE === 'true'` is set.
 *
 * IMPORTANT: this is a UI-only gate. Real authorisation (e.g. the
 * `/omega/genesis` route, write paths on protected APIs) is enforced
 * server-side by signed-request verification — see the route handler
 * comments. Never trust this flag for any security decision; it only
 * decides which nav items to render and whether to redirect away from
 * an admin URL.
 */

import { useAuthStore } from '@/stores/authStore';

/** Recognised founder addresses. Add new entries here if other admin
 *  identities are issued. Comparison is case-insensitive. */
export const ADMIN_EMAILS: ReadonlySet<string> = new Set([
  'yonathan@growing-intelligence.com',
  'shalev.yoni@gmail.com',
]);

export const ADMIN_STORAGE_KEY = 'gi-engine.admin';

function readAdminFlag(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(ADMIN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function readAdminEnv(): boolean {
  try {
    return import.meta.env.VITE_ADMIN_MODE === 'true';
  } catch {
    return false;
  }
}

function emailIsAdmin(email: string | undefined): boolean {
  if (!email) return false;
  return ADMIN_EMAILS.has(email.toLowerCase());
}

export function useIsAdmin(): boolean {
  const user = useAuthStore((s) => s.user);
  if (emailIsAdmin(user?.email)) return true;
  if (readAdminFlag()) return true;
  if (readAdminEnv()) return true;
  return false;
}

/** Non-hook variant for places that can't call hooks (route loaders, util fns). */
export function isAdminNow(): boolean {
  const email = useAuthStore.getState().user?.email;
  if (emailIsAdmin(email)) return true;
  if (readAdminFlag()) return true;
  if (readAdminEnv()) return true;
  return false;
}

/**
 * Strict founder gate — used to hide and protect OMEGA-tier surfaces.
 *
 * Unlike [`useIsAdmin`] this gate ONLY inspects the authenticated
 * user's email against [`ADMIN_EMAILS`]. It deliberately ignores:
 *   - the `gi-engine.admin` localStorage flag (UI-toy bypass — a
 *     non-founder browser console can flip it)
 *   - the `VITE_ADMIN_MODE` build env (dev convenience)
 *   - the `gi-engine.omega-admin` localStorage flag (set by
 *     visiting `/omega?key=founder` — a convenience, not a credential)
 *
 * If the user is not signed in or the email is not on the founder
 * allow-list, the gate is closed. The OMEGA dashboard, the OMEGA nav
 * item, and any kill-switch / refuse-list write surfaces all check
 * this — never `useIsAdmin`.
 *
 * Server-side identity is the actual security boundary. This is the
 * UI projection of "is this user a founder right now."
 */
export function useIsFounder(): boolean {
  const user = useAuthStore((s) => s.user);
  return emailIsAdmin(user?.email);
}

/** Non-hook variant of [`useIsFounder`]. Strict — email-only. */
export function isFounderNow(): boolean {
  return emailIsAdmin(useAuthStore.getState().user?.email);
}
